import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { api } from '../../services/api';
import { Item, InventoryPrecheckResponse } from '../../services/types';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';

interface Props {
  isOpen: boolean;
  initialItemId?: number | null;
  onClose: () => void;
  onSuccess: () => void;
  items: Item[];
}

export function StockAdjustmentModal({ isOpen, initialItemId = null, onClose, onSuccess, items }: Props) {
  const [itemId, setItemId] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [unitCost, setUnitCost] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'raw'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [profileFilter, setProfileFilter] = useState<'all' | 'GRC' | 'GRP' | 'BOM'>('all');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [precheckResult, setPrecheckResult] = useState<InventoryPrecheckResponse | null>(null);

  const normalizeItemType = (value?: string | null): string => String(value || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
  const isStockValuedItem = (item: Item): boolean => {
    const role = (item.item_role || '').toUpperCase();
    const type = normalizeItemType(item.item_type);
    return type === 'stock' && !['SERVICE', 'CONSUMABLE', 'NON_STOCK', 'NONSTOCK', 'ASSET', 'FIXED_ASSET'].includes(role);
  };
  const isRaw = (item: Item): boolean => isStockValuedItem(item) && (item.item_role || '').toUpperCase() !== 'FG';

  const stockItems = useMemo(() => items.filter((item) => isRaw(item)), [items]);
  const categoryOptions = useMemo(
    () => Array.from(new Set(stockItems.map((item) => String(item.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [stockItems]
  );
  const profileOptions = useMemo(() => {
    const profiles = new Set<string>();
    stockItems.forEach((item) => {
      (item.cost_profiles || []).forEach((profile) => profiles.add(String(profile).toUpperCase()));
      const category = String(item.category || '').toUpperCase();
      if (category === 'GRC' || category === 'GRP' || category === 'BOM') profiles.add(category);
    });
    return Array.from(profiles).sort() as Array<'GRC' | 'GRP' | 'BOM'>;
  }, [stockItems]);

  const filteredItems = useMemo(() => {
    return stockItems.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      if (q) {
        const haystack = `${item.code} ${item.name} ${item.category || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (roleFilter === 'raw' && !isRaw(item)) return false;
      if (categoryFilter !== 'all' && String(item.category || '') !== categoryFilter) return false;
      if (profileFilter !== 'all') {
        const itemProfiles = new Set((item.cost_profiles || []).map((profile) => String(profile).toUpperCase()));
        const fallback = String(item.category || '').toUpperCase();
        if (!itemProfiles.has(profileFilter) && fallback !== profileFilter) return false;
      }
      return true;
    });
  }, [stockItems, searchTerm, roleFilter, categoryFilter, profileFilter]);

  const selectedItem = stockItems.find((item) => item.id === itemId);

  useEffect(() => {
    if (!isOpen) return;
    setPrecheckResult(null);
    if (initialItemId) {
      setItemId(initialItemId);
      const item = stockItems.find((candidate) => candidate.id === initialItemId);
      if (item) {
        setUnitCost(item.wac_cost);
      }
      return;
    }
    setItemId(0);
  }, [isOpen, initialItemId, stockItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateRequiredFields([
      { key: 'item_id', label: 'Material', value: itemId, required: true },
      { key: 'quantity', label: 'Adjustment Qty', value: quantity === 0 ? '' : quantity, required: true },
      { key: 'reason', label: 'Reason / Reference', value: reason, required: true },
    ]);
    const nextErrors = { ...validation.errorsByField } as Record<string, string>;
    if (quantity > 0 && unitCost <= 0) nextErrors.unit_cost = 'Unit Cost must be greater than zero';
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      showValidationSummaryToast();
      focusFirstInvalidField(Object.keys(nextErrors)[0]);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    try {
      const precheck = await api.inventory.precheckAdjustment({
        item_id: itemId,
        quantity,
        reference_doc: reason.trim(),
      });
      setPrecheckResult(precheck);
      if (String(precheck.overall_status || '').toLowerCase() === 'fail') {
        toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Precheck failed');
        setIsSubmitting(false);
        return;
      }
      if (String(precheck.overall_status || '').toLowerCase() === 'warn') {
        toast.warning('Precheck warning: review impact before confirming adjustment.');
      }

      await api.inventory.createAdjustment({
        item_id: itemId,
        quantity,
        reason: reason.trim(),
        unit_cost: quantity > 0 ? unitCost : undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Adjustment failed', error);
      toast.error('Failed to create adjustment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background p-6 rounded-lg w-[420px] shadow-xl border">
        <h2 className="text-xl font-bold mb-4">Stock Adjustment</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-md border bg-slate-50 p-3 space-y-1">
            <p className="text-xs text-slate-600">
              Use adjustments for raw/material stock corrections only (count variance, damage, shrinkage, opening correction).
            </p>
            <p className="text-xs text-slate-500">
              Negative quantity reduces stock, positive quantity increases stock.
            </p>
            <p className="text-xs text-slate-500">
              Flow impact: adjustment updates inventory balances only; no accounting journal is posted.
            </p>
          </div>

          <div>
            <RequiredLabel required htmlFor="item_id" className="block mb-1">Material</RequiredLabel>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                className="col-span-2 w-full p-2 border rounded bg-background text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search code/name/category..."
                aria-label="Search Items"
              />
              <select
                className="w-full p-2 border rounded bg-background text-sm"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'all' | 'raw')}
                aria-label="Role Filter"
              >
                <option value="all">All Stock Roles</option>
                <option value="raw">Raw Materials</option>
              </select>
              <select
                className="w-full p-2 border rounded bg-background text-sm"
                value={profileFilter}
                onChange={(e) => setProfileFilter(e.target.value as 'all' | 'GRC' | 'GRP' | 'BOM')}
                aria-label="Cost Profile Filter"
              >
                <option value="all">All Cost Profiles</option>
                {profileOptions.map((profile) => (
                  <option key={profile} value={profile}>{profile}</option>
                ))}
              </select>
              <select
                className="col-span-2 w-full p-2 border rounded bg-background text-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Category Filter"
              >
                <option value="all">All Categories</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <select
              id="item_id"
              name="item_id"
              aria-invalid={!!fieldErrors.item_id}
              aria-required="true"
              className="w-full p-2 border rounded bg-background"
              value={itemId}
              onChange={(e) => {
                const id = Number(e.target.value);
                setItemId(id);
                const item = stockItems.find((i) => i.id === id);
                if (item) setUnitCost(item.wac_cost);
              }}
              required
              aria-label="Item Selector"
            >
              <option value={0}>Select Item</option>
              {filteredItems.map((item) => {
                const role = 'RAW';
                const profiles = (item.cost_profiles || []).join(',');
                const suffix = profiles ? ` | ${profiles}` : '';
                return (
                  <option key={item.id} value={item.id}>
                    [{role}] {item.code} - {item.name} ({item.category || 'Uncategorized'}){suffix}
                  </option>
                );
              })}
            </select>
            <FieldErrorText message={fieldErrors.item_id} />

            <p className="text-xs text-slate-500 mt-1">
              Showing {filteredItems.length} of {stockItems.length} stock items
            </p>
            {itemId > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Source context: include SO/WO/count reference for audit traceability.
              </p>
            )}
            {!filteredItems.length && (
              <p className="text-xs text-amber-600 mt-1">
                No items match current filters. Clear filters or search text.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <RequiredLabel required htmlFor="quantity" className="block mb-1">Adjustment Qty</RequiredLabel>
              <input
                id="quantity"
                name="quantity"
                aria-invalid={!!fieldErrors.quantity}
                aria-required="true"
                type="number"
                step="0.01"
                className="w-full p-2 border rounded bg-background"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                placeholder="e.g. -5.5 or 10"
                required
              />
              <FieldErrorText message={fieldErrors.quantity} />
              <p className="text-[10px] text-muted-foreground mt-1">
                Negative for reduction, Positive for addition.
              </p>
            </div>
            {quantity > 0 && (
              <div>
                <RequiredLabel required htmlFor="unit_cost" className="block mb-1">Unit Cost</RequiredLabel>
                <input
                  id="unit_cost"
                  name="unit_cost"
                  aria-invalid={!!fieldErrors.unit_cost}
                  aria-required="true"
                  type="number"
                  step="0.01"
                  className="w-full p-2 border rounded bg-background"
                  value={unitCost}
                  onChange={(e) => setUnitCost(Number(e.target.value))}
                  required
                />
                <FieldErrorText message={fieldErrors.unit_cost} />
              </div>
            )}
          </div>

          <div>
            <RequiredLabel required htmlFor="reason" className="block mb-1">Reason / Reference</RequiredLabel>
            <textarea
              id="reason"
              name="reason"
              aria-invalid={!!fieldErrors.reason}
              aria-required="true"
              className="w-full p-2 border rounded bg-background"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Damage, Year-end count, etc."
              required
            />
            <FieldErrorText message={fieldErrors.reason} />
          </div>

          {selectedItem && (
            <div className="p-2 bg-slate-50 rounded text-xs">
              <div className="flex justify-between">
                <span>Current Stock:</span>
                <span className="font-bold">{selectedItem.current_stock} {selectedItem.uom}</span>
              </div>
              <div className="flex justify-between">
                <span>New Stock:</span>
                <span className="font-bold">{(selectedItem.current_stock + quantity).toFixed(2)} {selectedItem.uom}</span>
              </div>
            </div>
          )}

          {precheckResult && (
            <div className="p-2 bg-slate-50 rounded text-xs space-y-1 border">
              <div className="font-semibold">
                Precheck: {String(precheckResult.overall_status || 'pass').toUpperCase()}
              </div>
              <div>Inventory: {precheckResult.impact?.inventory}</div>
              <div>Finance: {precheckResult.impact?.finance}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Save Adjustment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
